import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import toast from "react-hot-toast";
import API from "../../api";
import { LinearProgressBar } from "../Loading";
import RateTile from "./RateTile";
import PeriodFilter from "./PeriodFilter";

const TYPE_LABELS = { LATER: "Pré-enregistrement", NOW: "Sur place" };
const DAYS_OF_WEEK = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const TOOLTIP_STYLE = { backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px", boxShadow: "0 1px 3px rgba(16, 24, 40, 0.1)", fontSize: 12 };
const AXIS_TICK = { fontSize: 11, fill: "#94a3b8" };

const countOf = (rows, id) => (rows || []).find((row) => row._id === id)?.count || 0;

const AdmissionsDashboard = () => {
  const [range, setRange] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admission-stats", range],
    enabled: !!range,
    queryFn: async () => {
      const res = await API.get(`/stats/admissions?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`);
      if (!res.ok) throw new Error(res.message);
      return res.data;
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (error) toast.error(error.message || "Erreur lors du chargement des statistiques d'admission");
  }, [error]);

  const kpis = data?.kpis || { total: 0, noShow: 0, presentationRate: null };
  const funnel = data?.funnel || [];
  const funnelTop = funnel[0]?.count || 0;
  const funnelColors = ["#165DFB", "#1A4DCB", "#163A99"];
  const funnelSegmentWidth = funnel.length > 0 ? 100 / funnel.length : 100;
  const funnelData = funnel.map((step, index) => {
    const conversion = funnelTop > 0 ? Math.round((step.count / funnelTop) * 100) : 0;
    const leftHeight = funnelTop > 0 ? (step.count / funnelTop) * 100 : 0;
    const nextCount = index < funnel.length - 1 ? funnel[index + 1].count : step.count;
    const rightHeight = funnelTop > 0 ? (nextCount / funnelTop) * 100 : 0;
    const x0 = index * funnelSegmentWidth;
    const x1 = (index + 1) * funnelSegmentWidth;
    const points = `${x0},${50 - leftHeight / 2} ${x1},${50 - rightHeight / 2} ${x1},${50 + rightHeight / 2} ${x0},${50 + leftHeight / 2}`;
    return { step: step.step, count: step.count, conversion, points, color: funnelColors[index % funnelColors.length] };
  });
  const evolution = data?.evolution || [];
  const presentedTotal = evolution.reduce((sum, point) => sum + (point.presented || 0), 0);

  const dayMap = Object.fromEntries((data?.checkInByDayOfWeek || []).map((row) => [row._id, row.count]));
  const dayData = DAYS_OF_WEEK.map((label, index) => ({ label, count: dayMap[index + 1] || 0 }));

  const rateTiles = [
    { label: "Demandes", subtitle: "Total sur la période", value: kpis.total, data: evolution.map((point) => ({ date: point.date, value: point.created })), format: (value) => Math.round(value).toLocaleString("fr-FR") },
    { label: "Taux de présentation", subtitle: "Présentées / soumises", value: kpis.presentationRate, data: evolution.map((point) => ({ date: point.date, value: point.submitted > 0 ? Math.round((point.presented / point.submitted) * 100) : 0 })), variationUnit: "pt" },
    { label: "Présentées", subtitle: "Patients présentés", value: presentedTotal, data: evolution.map((point) => ({ date: point.date, value: point.presented })), format: (value) => Math.round(value).toLocaleString("fr-FR") },
    { label: "No-show", subtitle: "Jamais présentées", value: kpis.noShow, data: evolution.map((point) => ({ date: point.date, value: point.noShow })), format: (value) => Math.round(value).toLocaleString("fr-FR"), goodWhenUp: false },
  ];

  const typeItems = [
    { label: TYPE_LABELS.LATER, value: countOf(data?.byType, "LATER"), color: "bg-primary-500" },
    { label: TYPE_LABELS.NOW, value: countOf(data?.byType, "NOW"), color: "bg-slate-300" },
  ];
  const validationItems = (data?.validationMethod || []).map((row) => ({
    label: row.channel === "EMAIL" ? "Email" : "SMS",
    value: row.count,
    color: row.channel === "EMAIL" ? "bg-primary-500" : "bg-sky-400",
  }));
  const laboratoryItems = (data?.byLaboratory || []).map((row) => ({ label: row.name, value: row.count }));

  const typeTotal = typeItems.reduce((sum, item) => sum + item.value, 0);
  const validationTotal = validationItems.reduce((sum, item) => sum + item.value, 0);
  const laboratoryTotal = laboratoryItems.reduce((sum, item) => sum + item.value, 0);
  const laboratoryMax = Math.max(1, ...laboratoryItems.map((item) => item.value));

  return (
    <div className="space-y-4">
      <LinearProgressBar isVisible={isLoading} />

      <PeriodFilter onChange={setRange} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {rateTiles.map((tile) => (
          <RateTile key={tile.label} label={tile.label} subtitle={tile.subtitle} value={tile.value} data={tile.data} format={tile.format} variationUnit={tile.variationUnit} goodWhenUp={tile.goodWhenUp} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-gray-900">Tunnel de pré-enregistrement</h2>
            <p className="mt-0.5 text-xs text-gray-500">Conversion jusqu’à la présentation</p>
          </div>
          {funnelTop === 0 ? (
            <p className="py-10 text-center text-sm text-gray-400">Aucune donnée sur la période</p>
          ) : (
            <div className="relative w-full" style={{ height: 232 }}>
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
                {funnelData.map((entry) => (
                  <polygon key={entry.step} points={entry.points} fill={entry.color} />
                ))}
              </svg>
              <div className="absolute inset-0 flex">
                {funnelData.map((entry) => (
                  <div key={entry.step} className="flex flex-1 flex-col items-center justify-center px-1 text-center leading-tight text-white">
                    <span className="text-xs font-semibold">{entry.step}</span>
                    <span className="text-[11px] text-white/90">
                      {entry.count.toLocaleString("fr-FR")} · {entry.conversion}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 lg:col-span-2">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-gray-900">Évolution des demandes</h2>
            <p className="mt-0.5 text-xs text-gray-500">Créées, soumises et présentées par jour</p>
          </div>
          <ResponsiveContainer width="100%" height={232}>
            <LineChart data={evolution} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="date" tickLine={false} axisLine={false} tick={AXIS_TICK} minTickGap={24} />
              <YAxis tickLine={false} axisLine={false} tick={AXIS_TICK} allowDecimals={false} width={36} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ stroke: "#e5e7eb" }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Line type="monotone" dataKey="created" stroke="#165DFB" strokeWidth={2} name="Créées" dot={false} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="submitted" stroke="#94a3b8" strokeWidth={2} name="Soumises" dot={false} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="presented" stroke="#16a34a" strokeWidth={2} name="Présentées" dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-gray-900">Affluence par jour</h2>
            <p className="mt-0.5 text-xs text-gray-500">Présentations au laboratoire par jour de semaine</p>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dayData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={AXIS_TICK} />
              <YAxis tickLine={false} axisLine={false} tick={AXIS_TICK} allowDecimals={false} width={44} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#f8fafc" }} />
              <Bar dataKey="count" fill="#165DFB" radius={[3, 3, 0, 0]} name="Présentations" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-gray-900">Par laboratoire</h2>
            <p className="mt-0.5 text-xs text-gray-500">Top laboratoires</p>
          </div>
          {laboratoryTotal === 0 ? (
            <p className="py-10 text-center text-sm text-gray-400">Aucune donnée sur la période</p>
          ) : (
            <div className="space-y-3">
              {laboratoryItems.map((item) => (
                <div key={item.label}>
                  <div className="mb-1.5 flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-gray-600">{item.label}</span>
                    <span className="shrink-0 tabular-nums text-gray-500">
                      {item.value.toLocaleString("fr-FR")}
                      <span className="ml-1.5 text-gray-400">{Math.round((item.value / laboratoryTotal) * 100)}%</span>
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full rounded-full bg-primary-500" style={{ width: `${(item.value / laboratoryMax) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-gray-900">Type de demande</h2>
            <p className="mt-0.5 text-xs text-gray-500">Pré-enregistrement ou sur place</p>
          </div>
          {typeTotal === 0 ? (
            <p className="py-10 text-center text-sm text-gray-400">Aucune donnée sur la période</p>
          ) : (
            <>
              <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
                {typeItems.map((item) => (
                  <div key={item.label} className={item.color} style={{ width: `${(item.value / typeTotal) * 100}%` }} />
                ))}
              </div>
              <div className="mt-4 space-y-2">
                {typeItems.map((item) => (
                  <div key={item.label} className="flex items-center justify-between text-sm">
                    <span className="flex min-w-0 items-center gap-2 text-gray-600">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${item.color}`} />
                      <span className="truncate">{item.label}</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-gray-500">
                      {item.value.toLocaleString("fr-FR")}
                      <span className="ml-1.5 text-gray-400">{Math.round((item.value / typeTotal) * 100)}%</span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-gray-900">Canal de validation</h2>
            <p className="mt-0.5 text-xs text-gray-500">Identité confirmée par email ou SMS</p>
          </div>
          {validationTotal === 0 ? (
            <p className="py-10 text-center text-sm text-gray-400">Aucune donnée sur la période</p>
          ) : (
            <>
              <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
                {validationItems.map((item) => (
                  <div key={item.label} className={item.color} style={{ width: `${(item.value / validationTotal) * 100}%` }} />
                ))}
              </div>
              <div className="mt-4 space-y-2">
                {validationItems.map((item) => (
                  <div key={item.label} className="flex items-center justify-between text-sm">
                    <span className="flex min-w-0 items-center gap-2 text-gray-600">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${item.color}`} />
                      <span className="truncate">{item.label}</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-gray-500">
                      {item.value.toLocaleString("fr-FR")}
                      <span className="ml-1.5 text-gray-400">{Math.round((item.value / validationTotal) * 100)}%</span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdmissionsDashboard;
